import { Button, Loader } from '@mantine/core';
import { ACCOUNT, CHECKOUT_SUCCESS, ORDERS } from 'navigation/routes';
import QUERY_KEYS from 'network/config/queryKeys';
import {
  checkMadinaPaymentStatus,
  clearMadinaPendingPayment,
  pollMadinaPaymentStatus,
  readMadinaPendingPayment
} from 'network/services/madina';
import { getMadinaErrorMessage } from 'network/utils/madinaResponse';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { clearCardIdempotencyKey } from 'utils/madinaPayment';
import {
  extractMadinaOrderPaymentStatus,
  isMadinaOrderPaidFromPoll,
  isPendingPaymentStatus
} from 'utils/madinaPaymentStatus';

const CONFIRMING_MESSAGE = 'Confirming your payment…';
const STILL_CONFIRMING_MESSAGE = 'Payment is still confirming';
const SESSION_LOST_MESSAGE =
  'We could not find this payment on this device. Open Orders to check it. Do not start another card payment if that order is still pending.';

const CheckoutCallbackPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState(CONFIRMING_MESSAGE);
  const [lastStatus, setLastStatus] = useState('');
  const [isPolling, setIsPolling] = useState(true);
  const [sessionMissing, setSessionMissing] = useState(false);
  const hasCompletedRef = useRef(false);
  const pending = readMadinaPendingPayment();

  const handlePaymentConfirmed = useCallback(() => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;
    const orderId = pending.orderId;
    clearMadinaPendingPayment();
    clearCardIdempotencyKey();
    localStorage.removeItem('cart');
    queryClient.invalidateQueries([QUERY_KEYS.MADINA_ORDERS]);
    queryClient.invalidateQueries([QUERY_KEYS.CART]);
    queryClient.invalidateQueries([QUERY_KEYS.ALL_ORDERS]);
    enqueueSnackbar('Payment confirmed successfully', { variant: 'success' });
    const successPath = orderId
      ? `${CHECKOUT_SUCCESS}?order_id=${encodeURIComponent(String(orderId))}`
      : CHECKOUT_SUCCESS;
    navigate(successPath, { replace: true });
  }, [navigate, pending.orderId, queryClient]);

  const applyStatus = useCallback(
    (response: Awaited<ReturnType<typeof checkMadinaPaymentStatus>>) => {
      const status = extractMadinaOrderPaymentStatus(response);
      setLastStatus(status);
      if (isMadinaOrderPaidFromPoll(response)) {
        setMessage('Payment confirmed. Taking you to your order...');
        handlePaymentConfirmed();
        return;
      }
      if (isPendingPaymentStatus(status)) {
        setMessage(STILL_CONFIRMING_MESSAGE);
      }
    },
    [handlePaymentConfirmed]
  );

  const { mutate: confirmPayment } = useMutation({
    mutationFn: async (mode: 'poll' | 'once') => {
      if (!pending.orderId) {
        throw new Error(SESSION_LOST_MESSAGE);
      }

      const orderId = Number(pending.orderId);

      if (mode === 'once') {
        return checkMadinaPaymentStatus(orderId);
      }

      return pollMadinaPaymentStatus(orderId, {
        onStatus: status => {
          setLastStatus(status);
          if (isPendingPaymentStatus(status)) {
            setMessage(CONFIRMING_MESSAGE);
          }
        }
      });
    },
    onSuccess: applyStatus,
    onError: (error: unknown) => {
      setIsPolling(false);
      const errorMessage = getMadinaErrorMessage(error);
      const failed = /payment failed/i.test(errorMessage);

      if (failed) {
        clearCardIdempotencyKey();
        setMessage('This payment did not go through. You can try card checkout again.');
        enqueueSnackbar('Payment failed', { variant: 'error' });
        return;
      }

      setMessage(errorMessage || STILL_CONFIRMING_MESSAGE);
      if (!/still confirming|pending/i.test(errorMessage)) {
        enqueueSnackbar(errorMessage, { variant: 'warning' });
      }
    }
  });

  useEffect(() => {
    if (!pending.orderId) {
      setSessionMissing(true);
      setIsPolling(false);
      setMessage(SESSION_LOST_MESSAGE);
      return;
    }

    setIsPolling(true);
    confirmPayment('poll', {
      onSettled: () => {
        setIsPolling(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => {
    if (!pending.orderId) return;
    setMessage(CONFIRMING_MESSAGE);
    setIsPolling(true);
    confirmPayment('poll', {
      onSettled: () => {
        setIsPolling(false);
      }
    });
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
        {isPolling ? <Loader className="mx-auto mb-4" color="blue" /> : null}
        <h1 className="text-2xl font-bold mb-2">
          {sessionMissing ? 'Check your orders' : 'Confirming payment'}
        </h1>
        <p className="text-gray-600 mb-2">{message}</p>
        {lastStatus ? (
          <p className="text-sm text-gray-500 mb-6">
            Current status: <strong>{lastStatus}</strong>
          </p>
        ) : (
          <div className="mb-6" />
        )}
        <div className="flex flex-col gap-3">
          {!sessionMissing ? (
            <Button onClick={handleRetry} loading={isPolling}>
              Refresh
            </Button>
          ) : null}
          <Link to={`${ACCOUNT}/${ORDERS}`} className="text-blue-600 hover:underline">
            View my orders
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CheckoutCallbackPage;
